import React from 'react'
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil'
import {
	elasticFilterAtom,
	NextPageTrigger,
	searchLoadingAtom,
	searchQueryAtom,
	SearchResponseState,
} from './searchState'
import { getApp } from 'app'
import { activeTagsFromState } from 'modules-core/searchSystem/tags/tagMap'
import log from 'modules-core/log'

const logger = log.getLogger('search')

const { searchSystem } = getApp()

export const SearchEffects = () => {
	const set_searchLoading = useSetRecoilState(searchLoadingAtom)
	const [searchQuery, set_searchQuery] = useRecoilState(searchQueryAtom)
	const [searchFilters, set_searchFilters] = useRecoilState(elasticFilterAtom)
	const [searchResponse, set_searchResponse] =
		useRecoilState(SearchResponseState)
	const nextPageTrigger = useRecoilValue(NextPageTrigger)

	// Track abort controllers for cancellation
	const abortControllersRef = React.useRef<AbortController[]>([])

	React.useEffect(() => {
		set_searchQuery(searchSystem.getInitialQueryState())
		set_searchFilters(searchSystem.getInitialFilterState())
	}, [])

	React.useEffect(() => {
		if (searchQuery === null) return
		const activeTags = activeTagsFromState(searchFilters)
		searchSystem.updateMasterInterfaceUrl(searchQuery, activeTags)
		handleRequestNewSearch()
	}, [searchQuery, searchFilters])

	React.useEffect(() => {
		handleRequestNextPage()
	}, [nextPageTrigger])

	const handleRequestNewSearch = async () => {
		// Abort any in-flight requests.
		abortControllersRef.current.forEach((controller) => controller.abort())
		abortControllersRef.current = []

		// Create abort controller for this request,
		// so we can abort it when new search starts.
		const abortController = new AbortController()
		abortControllersRef.current.push(abortController)

		set_searchLoading(true)
		try {
			const response = await searchSystem.requestNewSearchUsingFilterState(
				searchQuery,
				searchFilters,
				{ signal: abortController.signal }
			)

			// Check if request was aborted
			if (abortController.signal.aborted) {
				return
			}

			if (!response) return

			const responseQuery = response?.query || ''
			if (searchQuery !== responseQuery) {
				logger.debug(
					'Stale query, abandon. Old query:',
					responseQuery,
					'New query:',
					searchQuery
				)
				return
			}

			set_searchResponse(response)
		} catch (err: any) {
			// Ignore abort errors
			if (err?.name === 'AbortError') {
				return
			}
			throw err
		} finally {
			// Remove this controller from the array when done.
			const index = abortControllersRef.current.indexOf(abortController)
			if (index > -1) {
				abortControllersRef.current.splice(index, 1)
			}
			if (!abortController.signal.aborted) {
				set_searchLoading(false)
			}
		}
	}

	const handleRequestNextPage = async () => {
		if (!searchResponse) return

		// Create abort controller for this request,
		// so we can abort it when new search starts.
		const abortController = new AbortController()
		abortControllersRef.current.push(abortController)

		try {
			const next = await searchSystem.requestNextPage({
				signal: abortController.signal,
			})

			// Check if request was aborted
			if (abortController.signal.aborted) {
				return
			}

			if (next === null) return

			// Verify this pagination is still for the current search
			if (next.query_hash !== searchResponse.query_hash) {
				logger.debug(
					'Stale pagination, abandon. Old hash:',
					next.query_hash,
					'New hash:',
					searchResponse.query_hash
				)
				return
			}

			set_searchResponse((prev) => ({
				...next,
				results: [...prev.results, ...next.results],
			}))
		} catch (err: any) {
			// Ignore abort errors
			if (err?.name === 'AbortError') {
				return
			}
			throw err
		} finally {
			// Remove this controller from the array when done.
			const index = abortControllersRef.current.indexOf(abortController)
			if (index > -1) {
				abortControllersRef.current.splice(index, 1)
			}
		}
	}
	return null
}
